import { mapConfig } from 'lib/config'

interface SearchRequestParams {
  at?: string // "lat,lng"
  limit: number
  q: string
  in?: string
  lang?: string
  politicalView?: string
}

interface SearchResultBaseItem {
  id: string
  resultType: string
  title: string
}

interface DiscoverRequestParams extends SearchRequestParams {
  lang?: string
  // show?: 'ta'
}

interface LookupRequestParams {
  id: string
  lang?: string
  // show?: 'ta'
}

interface GeocodeRequestParams {
  q: string
  lang?: string
}

interface ReverseGeocodeRequestParams {
  at: string // "lat,lng"
  lang?: string
  types?: string
}
interface BrowseRequestParams {
  at: string // "lat,lng"
  categories: string
  limit?: string
}

export interface SearchResultQueryItem extends SearchResultBaseItem {
  href: string
  resultType: 'categoryQuery' | 'chainQuery'
}

export interface SearchResultEntityItem extends SearchResultBaseItem {
  address: {
    label: string
    street: string
    houseNumber: string
  }
  mapView?: {
    east: number
    north: number
    south: number
    west: number
  }
  distance: number // meters
  position: {
    lat: number
    lng: number
  }
}

export interface MediaImagesItem {
  href: string
  date: string
  supplier: {
    id: string
  }
}

export interface MediaEditorialsItem {
  description: string
  language: string
  href: string
  supplier: {
    id: string
  }
}

export interface MediaRatingsItem {
  count: number
  average: number
  href: string
  supplier: {
    id: string
  }
}

export interface SearchResultEntityPlaceItemContacts {
  email?: {
    value: string
    categories?: {
      id: string
    }[]
  }[]
  phone?: {
    value: string
    categories?: {
      id: string
    }[]
  }[]
  www?: {
    value: string
    categories?: {
      id: string
    }[]
  }[]
}

interface SearchResultEntityPlaceItem extends SearchResultEntityItem {
  categories: {
    id: string
    name: string
    primary?: true
  }[]
  mapView?: {
    east: number
    north: number
    south: number
    west: number
  }
  contacts?: SearchResultEntityPlaceItemContacts[]
  openingHours?: {
    categories?: {
      id: string
    }[]
    isOpen: boolean
    text: string[]
  }[]
  media?: {
    images?: {
      items: MediaImagesItem[]
    }
    ratings?: {
      items: MediaRatingsItem[]
    }
    editorials?: {
      items: MediaEditorialsItem[]
    }
  }
}

export type AutosuggestResultItem =
  | SearchResultQueryItem
  | SearchResultEntityItem
  | SearchResultEntityPlaceItem
export type DiscoverResultItem = SearchResultEntityItem | SearchResultEntityPlaceItem
export type SearchServiceResultItem = AutosuggestResultItem | DiscoverResultItem

type ResultListHandler<Item> = (response: { items: Item[] }) => void
type ResultItemHandler<Item> = (response: Item) => void
type ErrorHandler = (error: Error) => void
type CancelableRequest = {
  cancel: () => void
}

// This interface is intended to be used as a substitute for "H.service.SearchService".
// It's needed because:
// * JSLA types are autogenerated in a way that "| null" is spread across "index.d.ts"
// * request params and responses are mapped to generic objects
export interface SearchService {
  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-autosuggest-brief.html
   */
  autosuggest: (
    params: SearchRequestParams,
    onResult: ResultListHandler<AutosuggestResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest

  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-browse-brief.html
   */
  browse: (
    params: BrowseRequestParams,
    onResult: ResultListHandler<DiscoverResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest

  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-discover-brief.html
   */
  discover: (
    params: SearchRequestParams,
    onResult: ResultListHandler<DiscoverResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest

  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-lookup-brief.html
   */
  lookup: (
    params: {
      id: string
      lang?: string
    },
    onResult: ResultItemHandler<DiscoverResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest

  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-geocode-brief.html
   */
  geocode: (
    params: {
      q: string
    },
    onResult: ResultListHandler<DiscoverResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest

  /**
   * @link https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/endpoint-reverse-geocode-brief.html
   */
  reverseGeocode: (
    params: ReverseGeocodeRequestParams,
    onResult: ResultListHandler<DiscoverResultItem>,
    onError: ErrorHandler
  ) => CancelableRequest
}

export type ResultItem = AutosuggestResultItem | DiscoverResultItem

function createSearchRequestParams(
  query: string,
  endpoint: 'autosuggest' | 'discover',
  at?: H.geo.IPoint,
  searchIn?: string,
  lang?: string,
  iso3CountryCode?: string
): SearchRequestParams {
  return {
    ...(searchIn === undefined && at && { at: `${at.lat},${at.lng}` }),
    ...(searchIn !== undefined && { in: searchIn }),
    ...(lang !== undefined && { lang }),
    ...(iso3CountryCode !== undefined && { politicalView: iso3CountryCode }),
    limit: mapConfig.services.search[endpoint].limit,
    q: query,
  }
}

export function createAutosuggestRequestParams(
  query: string,
  at: H.geo.IPoint,
  lang: string,
  iso3CountryCode?: string
): SearchRequestParams {
  return createSearchRequestParams(query, 'autosuggest', at, undefined, lang, iso3CountryCode)
}

export function createDiscoverRequestParams(
  query: string,
  lang: string,
  at?: H.geo.IPoint,
  searchIn?: string,
  iso3CountryCode?: string
): DiscoverRequestParams {
  return {
    ...createSearchRequestParams(query, 'discover', at, searchIn, lang, iso3CountryCode),
    // show: 'ta',
  }
}

// TODO: To be used in the future
export function createGeocodeRequestParams(query: string, lang: string): GeocodeRequestParams {
  return {
    lang,
    q: query,
  }
}
export function createReverseGeocodeRequestParams(
  at: H.geo.IPoint,
  lang: string,
  types?: string
): ReverseGeocodeRequestParams {
  const params: ReverseGeocodeRequestParams = {
    ...{ at: `${at.lat},${at.lng}` },
    ...{ lang },
  }

  if (types !== undefined) {
    params.types = types
  }

  return params
}

export function createLookupRequestParams(id: string, lang: string): LookupRequestParams {
  return {
    id,
    lang,
    // show: 'ta',
  }
}

// https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/result-types-query.html
export function isQueryItem(item: AutosuggestResultItem): boolean {
  return ['categoryQuery', 'chainQuery'].includes(item.resultType)
}

// https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/result-types-query.html
export function isQueryType(resultType: string): boolean {
  return ['categoryQuery', 'chainQuery'].includes(resultType)
}

// https://developer.here.com/documentation/geocoding-search-api/dev_guide/topics/result-types-address.html
export function isAddressItem(item: AutosuggestResultItem | DiscoverResultItem): boolean {
  return 'address' in item && !('categories' in item)
}
